import { createHash, createHmac } from "node:crypto";

/**
 * PII masking for ledger rows. Applied at the entity seam (entity.ts) so every
 * producer — webapp chat, channel gateway, a2a bridge, MCP plugin, voice relay —
 * is covered by the one write path. Regex-only by design: no model calls, no
 * dependencies, deterministic output. Placeholders are stable literals so masked
 * rows stay grep-able and idempotent (masking already-masked text is a no-op:
 * no placeholder matches any rule).
 *
 * Scope: email addresses, phone numbers, and secret-shaped strings. Structured
 * identifiers with their own formats (card PANs, government IDs, IBANs) and
 * postal addresses are not detected here.
 */

export const EMAIL_PLACEHOLDER = "[email]";
export const PHONE_PLACEHOLDER = "[phone]";
export const SECRET_PLACEHOLDER = "[secret]";

// ── Secret shapes ──────────────────────────────────────────────────────────
// Ordered most-specific → most-generic; each rule swallows its whole token so
// a later rule never re-matches a fragment of an earlier replacement.

/** Whole PEM private-key block, header/footer included. */
const PEM_BLOCK_RE =
  /-----BEGIN [A-Z0-9 ]{0,32}PRIVATE KEY-----[\s\S]*?-----END [A-Z0-9 ]{0,32}PRIVATE KEY-----/g;

/** JWT: dot-joined base64url segments whose first decodes to `{"` ("eyJ"). The
 *  signature segment is optional so unsigned/truncated tokens still match. */
const JWT_RE = /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{6,}(?:\.[A-Za-z0-9_-]{10,})?/g;

/** HTTP Authorization credential following a Bearer/Basic scheme word. The
 *  token must carry a digit or base64 punctuation so prose after "basic"
 *  ("the basic responsibilities of…") stays. */
const HTTP_CREDENTIAL_RE =
  /\b(Bearer|Basic)[ \t]+(?=[A-Za-z0-9._~+/=-]{0,80}[0-9+/=])[A-Za-z0-9._~+/=-]{16,}/gi;

/** Vendor key prefixes (OpenAI sk-/rk-, Slack xox?-, GitHub gh?_/github_pat_,
 *  GitLab glpat-, npm, AWS AKIA/ASIA, Google AIza/ya29., Docker, Shopify).
 *  Guarded by token-charset lookarounds, not \b, so a hyphenated word like
 *  "risk-assessment-…" can never expose an inner "sk-…" match. */
const KNOWN_PREFIX_RE =
  /(?<![A-Za-z0-9_-])(?:sk-[A-Za-z0-9_-]{16,}|rk-[A-Za-z0-9_-]{16,}|xox[a-z]-[A-Za-z0-9-]{10,}|gh[pousr]_[A-Za-z0-9]{16,}|github_pat_[A-Za-z0-9_]{20,}|glpat-[A-Za-z0-9_-]{20,}|npm_[A-Za-z0-9]{28,}|AKIA[0-9A-Z]{16}|ASIA[0-9A-Z]{16}|AIza[0-9A-Za-z_-]{35}|ya29\.[A-Za-z0-9_-]{20,}|dckr_pat_[A-Za-z0-9_-]{20,}|shp(?:at|ca|pa|ss)_[a-fA-F0-9]{32,})(?![A-Za-z0-9_-])/g;

/** Key/value assignment whose key NAME says secret, split by how much the name
 *  alone proves. STRONG names (password, api_key, client_secret, …) are
 *  config-shaped in any assignment, so any value of 8+ chars masks —
 *  `PASSWORD=correcthorsebatterystaple` has no digit and must still mask.
 *  WEAK names (secret, token, sig, authorization) appear in ordinary prose
 *  ("the secret: probably nothing"), so their unquoted value must carry a
 *  digit or base64 punctuation. Quoted values are deliberate config either
 *  way. The leading lookbehind allows `_`/`-` prefixes (env-var style) while
 *  rejecting matches inside an unrelated word; the key name and separator are
 *  kept so connection strings stay recognizable
 *  ("AccountKey=[secret];EndpointSuffix=…"). */
const KEYED_VALUE = "(\"[^\"\\n]{8,}\"|'[^'\\n]{8,}'|(?=[A-Za-z0-9+/=_.~%-]{0,64}[0-9+/=])[A-Za-z0-9+/=_.~%-]{8,})";
const KEYED_STRONG_SECRET_RE = new RegExp(
  "(?<![A-Za-z0-9])(shared[_-]?access[_-]?key|account[_-]?key|access[_-]?key|storage[_-]?key|api[_-]?key|apikey|x[_-]?api[_-]?key|client[_-]?secret|sas[_-]?token|access[_-]?token|refresh[_-]?token|auth[_-]?token|id[_-]?token|bot[_-]?token|private[_-]?key|password|passwd|pwd)([\"']?\\s*[:=]\\s*)(\"[^\"\\n]{8,}\"|'[^'\\n]{8,}'|[A-Za-z0-9+/=_.~%-]{8,})",
  "gi",
);
const KEYED_WEAK_SECRET_RE = new RegExp(
  "(?<![A-Za-z0-9])(authorization|secret|token|sig)([\"']?\\s*[:=]\\s*)" + KEYED_VALUE,
  "gi",
);

/** Telegram bot token (`<bot id>:AA<35 base64url>`): the credential operators
 *  paste to bind a channel bot, so it gets a dedicated whole-token rule (the
 *  keyed rule alone would stop at the `:` and leave the tail). */
const TELEGRAM_BOT_TOKEN_RE = /(?<!\d)\d{8,10}:AA[A-Za-z0-9_-]{32,34}(?![A-Za-z0-9_-])/g;

/** Spoken assignment ("my password is hunter2", "the pin is 4321") —
 *  vocabulary shared with the brain janitor's do-not-capture cues. The value
 *  MASKS BY DEFAULT; only a value that reads as ordinary state prose ("the api
 *  key is rotated") is exempted, by readsAsPasswordState. A quoted value is
 *  deliberate whatever it spells and masks even when multi-word; an unquoted
 *  multi-word passphrase is the documented residual — the regex takes one
 *  token, so only its first word masks. */
const SPOKEN_SECRET_RE =
  /\b((?:password|passcode|passphrase|api[ _-]?key|access code|pin(?: code)?)\s+is[ \t:]+)(?!\[)("[^"\n]{4,64}"|'[^'\n]{4,64}'|\S{4,64})/gi;

/** Punctuation that can trail a spoken value without belonging to it. Split
 *  off before masking and put back after, so a sentence keeps its shape
 *  ("…is outdated, I should rotate it"). '!' and '?' are deliberately absent:
 *  both are ordinary password characters, so they stay part of the value and
 *  are masked with it. */
const SENTENCE_TAIL_RE = /[.,;:]+$/;

/** What has to come off a value before it can be compared against the prose
 *  vocabulary: markdown emphasis around it, and sentence-final punctuation
 *  INCLUDING '!' and '?'. This strip decides only whether a value READS as
 *  prose; it never feeds the masked output, so treating '?' as punctuation
 *  here cannot shorten a real secret. */
const PROSE_TRIM_RE = /^[*_~`"']+|[*_~`"'.,;:!?]+$/g;

/**
 * States a password can be IN, as opposed to what a password IS.
 *
 * The direction of this list is the whole point, and it is the opposite of the
 * one that shipped first. That version asked "does this value look like a
 * credential?" and masked only on positive evidence — a digit, punctuation, an
 * internal capital, a long run. Every value that answered no was written to
 * the ledger verbatim, so `my password is Letmein`, `LETMEIN`, `let-me-in`,
 * `let.me.in` and `hunter٢` (a non-ASCII digit) all survived as plaintext
 * credentials. For a sanitizer that is the ONLY thing standing between a typed
 * password and durable storage, "no evidence" must never mean "safe".
 *
 * So the default is to MASK, and this list is the narrow exemption: a value
 * that is exactly one of these words is a sentence about a password, not a
 * password. The list is still incomplete — no English vocabulary is finite —
 * but incompleteness now fails the safe way. An unlisted state word
 * ("my password is unrecoverable") is masked: a cosmetic loss in a telemetry
 * snippet. An unlisted password shape is ALSO masked, which is the point.
 *
 * Kept deliberately tight: only words that cannot plausibly be someone's
 * password. Anything arguable belongs off this list.
 */
const PASSWORD_STATE_WORDS = new Set([
  "wrong", "incorrect", "invalid", "expired", "expiring", "required", "missing",
  "reset", "unchanged", "correct", "secure", "insecure", "weak", "strong",
  "blank", "empty", "gone", "lost", "forgotten", "compromised", "leaked",
  "breached", "outdated", "stale", "old", "new", "different", "same",
  "shared", "saved", "stored", "cached", "rotated", "rotating", "updated",
  "changed", "changing", "disabled", "enabled", "locked", "unlocked",
  "temporary", "permanent", "unrecoverable", "case-sensitive", "sensitive",
  "hashed", "encrypted", "written", "personal", "everywhere",
  "fine", "ok", "okay", "good", "bad", "ready", "set", "unset", "pending",
  "working", "broken", "failing", "failed", "accepted", "rejected",
  "not", "now", "still", "also", "only", "just", "being", "getting",
  "always", "never", "already", "again", "here", "there", "this", "that",
]);

/**
 * Is this spoken value a sentence about a password rather than the password?
 *
 * Quoted values are never exempt: quoting is deliberate, and `password is
 * "wrong"` is far more likely to be someone quoting their actual value than
 * discussing its state.
 */
function readsAsPasswordState(value: string): boolean {
  if (/^["']/.test(value)) return false;
  return PASSWORD_STATE_WORDS.has(value.replace(PROSE_TRIM_RE, "").toLowerCase());
}

/** Mask a spoken value, keeping any sentence punctuation that trailed it. */
function maskSpokenValue(lead: string, value: string): string {
  const tail = SENTENCE_TAIL_RE.exec(value)?.[0] ?? "";
  return `${lead}${SECRET_PLACEHOLDER}${tail}`;
}

/** Standalone long mixed-alphabet token (base64-ish): 40+ chars with upper +
 *  lower + digit in the first 64. Deliberately excludes '.' and '/' so paths,
 *  URLs, and dotted hosts break into short runs; hex git SHAs (no uppercase)
 *  and UUIDs (36 chars) fall outside the rule. */
const B64ISH_RE =
  /(?<![A-Za-z0-9+=_-])(?=[A-Za-z0-9+=_-]{0,64}[A-Z])(?=[A-Za-z0-9+=_-]{0,64}[a-z])(?=[A-Za-z0-9+=_-]{0,64}[0-9])[A-Za-z0-9+=_-]{40,}(?![A-Za-z0-9+=_-])/g;

// ── Contact shapes ─────────────────────────────────────────────────────────

/** Email: local@domain.tld. Requires a dotted TLD so "@mention", "user@host",
 *  and "meet @ 5pm" never match. Unicode letters/digits are part of both the
 *  local part and the domain so internationalized addresses (用户@example.com,
 *  ilan@例え.jp) mask like ASCII ones. */
const EMAIL_RE =
  /[\p{L}\p{N}._%+-]+@[\p{L}\p{N}](?:[\p{L}\p{N}.-]*[\p{L}\p{N}])?\.\p{L}{2,}(?![\p{L}\p{N}-])/gu;

/** International phone: '+' then 7–15 digits with optional separators. The
 *  digit/dot/slash/hyphen lookbehind keeps arithmetic ("5+3…") and ranges out. */
const PHONE_INTL_RE = /(?<![\d/.-])\+\d{1,3}(?:[ .-]?\(\d{1,4}\))?(?:[ .-]?\d){6,12}(?!\d)/g;

/** Separated phone shapes: 3-3-4 with optional leading country digit
 *  ((415) 555-2671, 415-555-2671, 1-800-555-0199), parenthesized area code
 *  with tight digits ((415)5552671), and 0-led local 2/3 + 7 (054-1234567).
 *  Outside the parenthesized form the separators are REQUIRED — a bare
 *  10-digit run (order id, timestamp) never matches — and the lookarounds
 *  reject digit/dot/hyphen neighbors so ISO dates and dotted IPs can't yield
 *  a partial match. */
const PHONE_SEP_RE =
  /(?<![\d.-])(?:\d[ .-])?(?:\(\d{3}\)[ .-]?\d{3}[ .-]?\d{4}|\d{3}[ .-]\d{3}[ .-]\d{4}|0\d{1,2}[ .-]\d{7})(?![\d-])/g;

/** Bare digit run in explicit phone context ("call me at 4155552671",
 *  "phone number is 4155552671"). The cue vocabulary is deliberately tight —
 *  telephony verbs and phone nouns only — so "order number is 1234567890" and
 *  bare ids never match. */
const PHONE_CONTEXT_RE =
  /\b((?:call|text|dial|whatsapp)(?:\s+(?:me|us|him|her|them|back))?(?:\s+(?:at|on))?\s*[:-]?\s*|(?:phone|mobile|cell|tel)(?:\s*(?:number|no\.?))?\s*(?:is|:)?\s*)(?<![\d.(-])\d{7,15}(?![\d.])/gi;

/** Openers that make everything after them secret when their closer is
 *  missing: a PEM header with no footer, or a secret-named assignment whose
 *  quoted value never closes. Text can arrive cut mid-construct (the scan
 *  window, a transport truncation), and the head of a cut-open secret is
 *  still a secret — so an unclosed opener fails CLOSED: the opener and
 *  everything after it become the placeholder. */
const PEM_OPEN_RE = /-----BEGIN [A-Z0-9 ]{0,32}PRIVATE KEY-----/;
const UNCLOSED_QUOTED_SECRET_RE =
  /(?<![A-Za-z0-9])(?:shared[_-]?access[_-]?key|account[_-]?key|access[_-]?key|storage[_-]?key|api[_-]?key|apikey|x[_-]?api[_-]?key|client[_-]?secret|sas[_-]?token|access[_-]?token|refresh[_-]?token|auth[_-]?token|id[_-]?token|bot[_-]?token|private[_-]?key|password|passwd|pwd|authorization|secret|token|sig)["']?\s*[:=]\s*["'][^"'\n]*$/i;

function failClosedOnCutConstructs(text: string): string {
  const pem = PEM_OPEN_RE.exec(text);
  if (pem && !/-----END [A-Z0-9 ]{0,32}PRIVATE KEY-----/.test(text.slice(pem.index))) {
    return text.slice(0, pem.index) + SECRET_PLACEHOLDER;
  }
  const quoted = UNCLOSED_QUOTED_SECRET_RE.exec(text);
  if (quoted) return text.slice(0, quoted.index) + SECRET_PLACEHOLDER;
  return text;
}

/** Mask secret-shaped strings only. Never optional: pasted credentials do not
 *  belong in any deployment's rows, so no policy switch reaches this layer. */
export function maskSecrets(text: string): string {
  const masked = text
    .replace(PEM_BLOCK_RE, SECRET_PLACEHOLDER)
    .replace(JWT_RE, SECRET_PLACEHOLDER)
    .replace(HTTP_CREDENTIAL_RE, (_, scheme: string) => `${scheme} ${SECRET_PLACEHOLDER}`)
    .replace(KNOWN_PREFIX_RE, SECRET_PLACEHOLDER)
    .replace(TELEGRAM_BOT_TOKEN_RE, SECRET_PLACEHOLDER)
    .replace(KEYED_STRONG_SECRET_RE, (_, key: string, sep: string) => `${key}${sep}${SECRET_PLACEHOLDER}`)
    .replace(KEYED_WEAK_SECRET_RE, (_, key: string, sep: string) => `${key}${sep}${SECRET_PLACEHOLDER}`)
    // Fail closed: an explicit credential statement masks its value unless the
    // value is recognisably a word ABOUT a password rather than one.
    .replace(SPOKEN_SECRET_RE, (whole: string, lead: string, value: string) =>
      readsAsPasswordState(value) ? whole : maskSpokenValue(lead, value))
    .replace(B64ISH_RE, SECRET_PLACEHOLDER);
  return failClosedOnCutConstructs(masked);
}

/** Mask contact shapes (emails, phone numbers) only. */
export function maskContact(text: string): string {
  return text
    .replace(EMAIL_RE, EMAIL_PLACEHOLDER)
    .replace(PHONE_INTL_RE, PHONE_PLACEHOLDER)
    .replace(PHONE_SEP_RE, PHONE_PLACEHOLDER)
    .replace(PHONE_CONTEXT_RE, (_, lead: string) => `${lead}${PHONE_PLACEHOLDER}`);
}

/** Bare digit run of phone length. maskContact deliberately leaves these
 *  alone (order ids, timestamps stay readable in telemetry) — but in a
 *  conversation owned by someone an install SERVES, a bare run usually IS the
 *  identifier ("What's your number?" → "0541234567"). Decimals and
 *  word-adjacent runs stay; a sentence-final period does not protect a run. */
const BARE_PHONE_RUN_RE = /(?<![\w.,-])\d{7,15}(?!\.?\d)(?![\w,-])/g;

/** maskContact plus bare phone-length digit runs — the strict transcript mode
 *  for conversations whose owner is not the install's own team. */
export function maskContactStrict(text: string): string {
  return maskContact(text).replace(BARE_PHONE_RUN_RE, PHONE_PLACEHOLDER);
}

/** Mask emails, phone numbers, and secret-shaped strings in free text. */
export function maskPii(text: string): string {
  return maskContact(maskSecrets(text));
}

// ── userRef pseudonymization ───────────────────────────────────────────────

/** Digest scheme version, embedded in every digest so a future key-rotation
 *  or algorithm change can dual-read old and new forms side by side. */
const USERREF_DIGEST_VERSION = "1";

/** The digest key is per-install, so the same address yields DIFFERENT
 *  digests on different installs — one person is never linkable across
 *  deployments, and a rainbow table built for one install is useless on
 *  another. Within an install every writer and the read side's TeamMembers
 *  handle join must agree, so the key comes from shared deployment state:
 *  `M8T_LEDGER_USERREF_KEY` when the operator sets one, else derived from the
 *  install's storage account name (present in every gateway-side process),
 *  else a static fallback for keyless dev runs. Changing the key splits
 *  identities at that point in the row history — set it before first use and
 *  leave it. Pseudonymization, not a secrecy boundary. */
function userRefHashKey(): string {
  return (
    process.env.M8T_LEDGER_USERREF_KEY ??
    (process.env.STORAGE_ACCOUNT_NAME
      ? `m8t-ledger-userref:${process.env.STORAGE_ACCOUNT_NAME}`
      : "m8t-agent-ledger-userref-dev")
  );
}

/** One digested segment: `<kind>#<version>:<32 hex>`. */
function digestSegment(kind: "email" | "key", value: string): string {
  const hex = createHmac("sha256", userRefHashKey()).update(value).digest("hex").slice(0, 32);
  return `${kind}#${USERREF_DIGEST_VERSION}:${hex}`;
}

/** A delegated owner identity: `svc:<principalOid>:<endUserKey>`, minted where
 *  an authorized service caller asserts an end user (the webapp's end-user-key
 *  seam). The third segment is that CALLER-ASSERTED key — for a public visitor
 *  it is the credential their session is proven by, so it can never be stored.
 *  Recognized by the literal `svc:` prefix, which an interactive user's
 *  `<tenantId>:<oid>` cannot carry: the identity seam refuses a tenant id equal
 *  to (or containing) that prefix precisely so the two shapes stay distinct. */
const DELEGATED_REF_RE = /^(svc:[^:]*:)(.+)$/;

/** A segment this function already produced. Digesting a digest would change
 *  the value on every pass and split one person's rows across the boundary.
 *  A raw key cannot spell one: the published end-user-key alphabet is
 *  `[A-Za-z0-9._-]`, so neither `#` nor `:` can appear in a caller's value and
 *  no assertion can dress itself up as already-digested. */
const DIGESTED_SEGMENT_RE = /^(?:email|key)#\d+:/;

/**
 * Replace the identifying parts of a userRef with stable opaque digests.
 *
 * Every email-shaped part becomes `email#1:<32 hex>`: a raw UPN becomes one
 * digest, and a composite ref keeps its structure (`svc:<oid>:a@b.com` →
 * `svc:<oid>:email#1:…`). A delegated ref's bare key segment becomes
 * `key#1:<32 hex>` the same way, keeping the `svc:<oid>:` prefix so the
 * asserting principal stays readable and the shape stays recognizable.
 *
 * Everything else — `<tenantId>:<oid>`, channel handles, a2a agent names, OS
 * usernames — passes through verbatim: already opaque or roster-scoped, and
 * the read side resolves them (TeamMembers join, delegation display).
 *
 * An address is lowercased before hashing so case-variant spellings of one
 * address stay one user. A key is NOT: it is case-sensitive, and folding case
 * would map two distinct end users onto one digest.
 */
export function maskUserRef(ref: string | undefined): string | undefined {
  if (ref == null) return undefined;
  const withEmailsDigested = ref.replace(EMAIL_RE, (m) => digestSegment("email", m.toLowerCase()));
  return maskDelegatedKey(withEmailsDigested);
}

/**
 * Digest ONLY the delegated end-user key, leaving every other part of the ref
 * exactly as it came in.
 *
 * This is the half of maskUserRef that is NOT the operator's to switch off. The
 * key segment of `svc:<principalOid>:<endUserKey>` is the visitor's own
 * `__Host-m8t_session` cookie — a bearer credential, not a contact detail — and
 * `M8T_LEDGER_CONTACT_MASKING=off` is an opt-out for an operator's own team's
 * contact info, not a licence to persist someone else's live session token.
 * Secret masking has never had a switch (see maskSecrets); this closes the one
 * path where a credential was riding in an identity field and inherited the
 * contact switch by accident.
 */
export function maskDelegatedKey(ref: string | undefined): string | undefined {
  if (ref == null) return undefined;
  const delegated = DELEGATED_REF_RE.exec(ref);
  if (!delegated) return ref;
  const [, prefix, key] = delegated;
  if (DIGESTED_SEGMENT_RE.test(key)) return ref;
  return `${prefix}${digestSegment("key", key)}`;
}

/**
 * The conversation-registry owner digest for a delegated ref —
 * `sha256-base64url(<owner identity>)`, the same value the webapp writes as the
 * digest half of a conversation's `ownerKey` and as its registry partition key.
 * `undefined` for every other ref shape.
 *
 * Recorded on the row BECAUSE the ref itself is now a pseudonym: retention
 * (`m8t conversations sweep`) has to prove a conversation belongs to the
 * visitor its rows attribute it to, and the raw key it used to recompute that
 * from is deliberately gone. This is a digest of a delegated identity, not the
 * identity — and it is the same digest already recorded on the conversation,
 * so nothing new about the end user is stored.
 */
export function delegatedOwnerDigest(ref: string | undefined): string | undefined {
  if (!ref?.startsWith("svc:")) return undefined;
  return createHash("sha256").update(ref).digest("base64url");
}
